import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Accordion from "../components/Accordion";
import BilansTreeView from "../components/BilansTreeView";
import { useAuth } from "../context/AuthContext";
import { TutorCodeCard } from "./TutorSpace";
import {
    Plus, Calendar, GraduationCap, ArrowRight, Inbox, CheckCircle2, Eye, Search, Lock,
    Building2, Briefcase, Bell, AlertTriangle, ChevronRight, BookOpen, TrendingUp, CalendarClock,
    Users, BarChart3, Filter, KeyRound, FolderTree, List, Clock3,
} from "lucide-react";

const FILIERE_LABELS = {
    electrotechnique: "BTS Électrotechnique",
    ciel: "BTS CIEL",
};

const FRENCH_MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

function periodLabel(b) {
    // Group key based on the start date month-year, e.g. "Févr. 2026"
    if (!b.date_debut) return "Période inconnue";
    try {
        const [y, m] = b.date_debut.split("-");
        const monthName = FRENCH_MONTHS[parseInt(m, 10) - 1] || "";
        return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${y}`;
    } catch {
        return "Période inconnue";
    }
}

function periodSortKey(b) {
    return b.date_debut || "0000-00-00";
}

/** Compute notification flags from the tutor evaluation. */
function computeAlerts(b) {
    const alerts = [];
    const ev = b.tutor_evaluation;
    if (!ev) return alerts;
    if (ev.assiduite === "absences_concerning") {
        alerts.push({ level: "high", label: `Assiduité préoccupante${ev.nb_absences ? ` (${ev.nb_absences} absences)` : ""}` });
    }
    if (ev.note_globale != null && ev.note_globale < 10) {
        alerts.push({ level: "high", label: `Note globale faible : ${ev.note_globale}/20` });
    }
    if ((ev.ponctualite && ev.ponctualite <= 2) || (ev.serieux_motivation && ev.serieux_motivation <= 2)) {
        alerts.push({ level: "med", label: "Comportement professionnel à surveiller" });
    }
    if (ev.points_a_ameliorer && ev.points_a_ameliorer.length > 200) {
        alerts.push({ level: "low", label: "Nombreux points à améliorer signalés" });
    }
    return alerts;
}

export default function Dashboard() {
    const { user } = useAuth();
    const [bilans, setBilans] = useState([]);
    const [alternants, setAlternants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filiereFilter, setFiliereFilter] = useState("all");
    const [niveauFilter, setNiveauFilter] = useState("all");
    const [groupBy, setGroupBy] = useState("none"); // none | period | niveau | filiere
    const [viewMode, setViewMode] = useState(() => {
        // Default to tree view; remember user preference per session.
        try { return sessionStorage.getItem("profawz-dashboard-view") || "tree"; }
        catch { return "tree"; }
    });
    const [treeReloadKey, setTreeReloadKey] = useState(0);
    const setView = (mode) => {
        setViewMode(mode);
        try { sessionStorage.setItem("profawz-dashboard-view", mode); } catch { /* ignore */ }
    };

    const isFormateur = user?.role === "formateur" || user?.role === "admin" || user?.role === "responsable";
    const isResponsable = user?.role === "responsable" || user?.role === "admin";

    const reloadBilans = useCallback(async () => {
        try {
            const requests = [api.get("/bilans")];
            if (isFormateur) requests.push(api.get("/establishment/alternants"));
            const results = await Promise.all(requests);
            setBilans(results[0].data);
            if (isFormateur && results[1]) setAlternants(results[1].data);
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
        // Signal the tree view to refetch as well
        setTreeReloadKey((k) => k + 1);
    }, [isFormateur]);

    useEffect(() => { reloadBilans(); }, [reloadBilans]);

    const filtered = useMemo(() => {
        return bilans.filter((b) => {
            if (filiereFilter !== "all" && b.filiere !== filiereFilter) return false;
            if (niveauFilter !== "all" && b.niveau !== niveauFilter) return false;
            if (search) {
                const s = search.toLowerCase();
                const hay = `${b.prenom} ${b.nom} ${b.tuteur} ${b.entreprise || ""} ${b.author_email || ""}`.toLowerCase();
                if (!hay.includes(s)) return false;
            }
            return true;
        });
    }, [bilans, search, filiereFilter, niveauFilter]);

    const grouped = useMemo(() => {
        if (groupBy === "none") return [{ key: "all", label: null, items: filtered }];
        const map = new Map();
        filtered.forEach((b) => {
            let key, label, sort;
            if (groupBy === "period") {
                key = periodLabel(b);
                label = key;
                sort = periodSortKey(b);
            } else if (groupBy === "niveau") {
                key = b.niveau;
                label = b.niveau === "1ere" ? "1ère année" : "2ème année";
                sort = b.niveau;
            } else {
                key = b.filiere;
                label = FILIERE_LABELS[b.filiere] || b.filiere;
                sort = b.filiere;
            }
            if (!map.has(key)) map.set(key, { key, label, sort, items: [] });
            map.get(key).items.push(b);
        });
        const groups = Array.from(map.values());
        if (groupBy === "period") groups.sort((a, b) => b.sort.localeCompare(a.sort));
        else groups.sort((a, b) => a.sort.localeCompare(b.sort));
        return groups;
    }, [filtered, groupBy]);

    // Notifications: bilans with at least one alert
    const alerted = useMemo(() => {
        if (!isFormateur) return [];
        return bilans
            .map((b) => ({ b, alerts: computeAlerts(b) }))
            .filter((x) => x.alerts.length > 0);
    }, [bilans, isFormateur]);

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="dashboard-page">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
                    <div>
                        <div className="label-eyebrow mb-2">{isFormateur ? "Espace formateur" : "Espace alternant"}</div>
                        <h1 className="h-display text-4xl sm:text-5xl font-semibold leading-tight">
                            {isFormateur ? (
                                <>Bilans de tous<br /><span className="text-orange">les alternants</span></>
                            ) : (
                                <>Bonjour <span className="text-orange">{user?.prenom || user?.email?.split("@")[0]}</span></>
                            )}
                        </h1>
                        {isFormateur && (
                            <p className="mt-3 text-sm text-muted2 max-w-xl">
                                Vous voyez en lecture seule l&apos;ensemble des bilans soumis par les alternants de la plateforme.
                            </p>
                        )}
                    </div>
                    {!isFormateur && (
                        <Link to="/bilans/nouveau" className="btn-primary !py-3 self-start sm:self-auto" data-testid="dashboard-new-bilan-button">
                            <Plus size={16} /> Nouveau bilan d&apos;alternance
                        </Link>
                    )}
                    {isFormateur && (
                        <div className="flex flex-wrap gap-2 self-start sm:self-auto">
                            <Link to="/cours" className="btn-primary !py-3" data-testid="dashboard-courses-cta">
                                <BookOpen size={16} /> Saisir un cours / TP
                            </Link>
                            <Link to="/suivi-formateurs" className="btn-outline !py-3" data-testid="dashboard-tracking-cta">
                                <Clock3 size={16} /> Suivi formateurs
                            </Link>
                            {isResponsable && (
                                <>
                                    <Link to="/agenda" className="btn-outline !py-3" data-testid="dashboard-agenda-cta">
                                        <CalendarClock size={16} /> Agenda RDV
                                    </Link>
                                    <Link to="/bulletins" className="btn-outline !py-3" data-testid="dashboard-bulletins-cta">
                                        <Briefcase size={16} /> Bulletins
                                    </Link>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* ALTERNANT — tutor code card */}
                {!isFormateur && user?.tutor_code && (
                    <div className="mb-6">
                        <TutorCodeCard tutorCode={user.tutor_code} />
                    </div>
                )}

                {/* COMPACT ACCORDIONS (closed by default) */}
                <div className="space-y-3 mb-8">
                    {/* QUICK GUIDE */}
                    {isFormateur && (
                        <Accordion
                            icon={ArrowRight}
                            title="Guide rapide"
                            subtitle="Cours · Progression · Agenda"
                            testId="dash-quick-guide"
                        >
                            <div className="grid sm:grid-cols-3 gap-3">
                                <Link to="/cours" className="panel p-4 hover:border-orange transition-colors group" data-testid="dashboard-tile-cours">
                                    <BookOpen size={22} className="text-orange mb-2" strokeWidth={1.5} />
                                    <div className="font-semibold text-sm">Cours & TPs</div>
                                    <div className="text-[11px] text-muted2 mt-0.5 leading-snug">Saisir une séance, taguer les alternants présents et évaluer chaque compétence du référentiel.</div>
                                    <span className="text-[10px] font-mono text-orange mt-2 inline-flex items-center gap-1 group-hover:gap-2 transition-all">Ouvrir <ArrowRight size={10} /></span>
                                </Link>
                                <Link to={`/progression/${alternants[0]?.id || user?.id}`} className="panel p-4 hover:border-orange transition-colors group" data-testid="dashboard-tile-progression">
                                    <TrendingUp size={22} className="text-orange mb-2" strokeWidth={1.5} />
                                    <div className="font-semibold text-sm">Progression alternant</div>
                                    <div className="text-[11px] text-muted2 mt-0.5 leading-snug">Vue agrégée école + entreprise par compétence avec écarts et export PDF.</div>
                                    <span className="text-[10px] font-mono text-orange mt-2 inline-flex items-center gap-1 group-hover:gap-2 transition-all">Voir un alternant <ArrowRight size={10} /></span>
                                </Link>
                                {isResponsable ? (
                                    <Link to="/agenda" className="panel p-4 hover:border-orange transition-colors group" data-testid="dashboard-tile-agenda">
                                        <CalendarClock size={22} className="text-orange mb-2" strokeWidth={1.5} />
                                        <div className="font-semibold text-sm">Agenda · RDV tuteurs</div>
                                        <div className="text-[11px] text-muted2 mt-0.5 leading-snug">Proposez des créneaux, partagez le lien public à vos tuteurs entreprise.</div>
                                        <span className="text-[10px] font-mono text-orange mt-2 inline-flex items-center gap-1 group-hover:gap-2 transition-all">Ouvrir <ArrowRight size={10} /></span>
                                    </Link>
                                ) : (
                                    <div className="panel p-4 bg-surface" data-testid="dashboard-tile-agenda-info">
                                        <CalendarClock size={22} className="text-muted2 mb-2" strokeWidth={1.5} />
                                        <div className="font-semibold text-sm text-muted2">Agenda · RDV tuteurs</div>
                                        <div className="text-[11px] text-muted2 mt-0.5 leading-snug">Réservé au Responsable de formation.</div>
                                    </div>
                                )}
                            </div>
                        </Accordion>
                    )}

                    {/* ALTERNANTS LIST */}
                    {isFormateur && alternants.length > 0 && (
                        <Accordion
                            icon={Users}
                            title="Vos alternants"
                            subtitle="Cliquez pour voir la progression complète"
                            count={alternants.length}
                            testId="dash-alternants"
                        >
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {alternants.map((a) => (
                                    <AlternantCard key={a.id} a={a} onNiveauChange={reloadBilans} />
                                ))}
                            </div>
                        </Accordion>
                    )}

                    {/* INVITE CODE */}
                    {isFormateur && user?.invite_code && (
                        <Accordion
                            icon={KeyRound}
                            title="Code d'établissement"
                            subtitle="À partager à vos alternants pour l'inscription"
                            testId="dash-invite-code"
                            rightSlot={
                                <code className="bg-orange text-white px-2.5 py-1 font-mono text-xs font-bold tracking-wider" data-testid="invite-code-chip">
                                    {user.invite_code}
                                </code>
                            }
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-ink text-white p-5">
                                <div>
                                    <div className="label-eyebrow text-orange/80 mb-1">Code d&apos;établissement</div>
                                    <h3 className="h-display text-lg font-semibold">Partagez ce code</h3>
                                    <p className="text-xs text-white/60 mt-1.5 max-w-md">
                                        Vos étudiants doivent saisir ce code à l&apos;inscription pour rejoindre votre espace.
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <code className="bg-orange text-white px-4 py-2 font-mono text-lg font-bold tracking-wider" data-testid="invite-code-value">
                                        {user.invite_code}
                                    </code>
                                    <button
                                        onClick={() => { navigator.clipboard?.writeText(user.invite_code); }}
                                        className="btn-outline !bg-transparent !text-white !border-white/30 hover:!bg-white/10"
                                        data-testid="invite-code-copy"
                                    >
                                        Copier
                                    </button>
                                </div>
                            </div>
                        </Accordion>
                    )}

                    {/* ALERTS */}
                    {isFormateur && alerted.length > 0 && (
                        <Accordion
                            icon={Bell}
                            title="Alertes à examiner"
                            subtitle="Assiduité, notes basses, comportement"
                            count={alerted.length}
                            defaultOpen={true}
                            accent="border-l-destructive"
                            testId="dash-alerts"
                        >
                            <ul className="divide-y divide-rule">
                                {alerted.slice(0, 8).map(({ b, alerts }) => (
                                    <li key={b.id}>
                                        <Link to={`/bilans/${b.id}`} className="flex items-start justify-between gap-3 py-3 hover:bg-surface px-2 -mx-2 transition-colors" data-testid={`notif-${b.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium text-sm">
                                                    {b.prenom} {b.nom}
                                                    <span className="text-muted2 font-mono text-[11px] ml-2">· {FILIERE_LABELS[b.filiere]} · {b.niveau === "1ere" ? "1ère" : "2ème"}</span>
                                                </div>
                                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                    {alerts.map((a) => (
                                                        <span key={a.label} className={`pill inline-flex items-center ${a.level === "high" ? "bg-red-100 text-red-800 border border-red-200" : a.level === "med" ? "bg-amber-100 text-amber-800 border border-amber-200" : "bg-rule text-ink/70"}`}>
                                                            <AlertTriangle size={10} className="mr-1" /> {a.label}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            <ChevronRight size={16} className="text-muted2 mt-1.5 shrink-0" />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                            {alerted.length > 8 && (
                                <div className="mt-3 text-xs font-mono text-muted2 text-center">
                                    +{alerted.length - 8} autre(s) alerte(s) ailleurs dans la liste
                                </div>
                            )}
                        </Accordion>
                    )}

                    {/* STATS */}
                    <Accordion
                        icon={BarChart3}
                        title="Statistiques"
                        subtitle={`${bilans.length} bilan(s) · ${bilans.filter((b) => (b.photo_paths?.length || 0) > 0).length} avec photos · ${bilans.filter((b) => b.tutor_evaluation).length} tuteurs évalués`}
                        testId="dash-stats"
                    >
                        <div className="grid sm:grid-cols-4 gap-px bg-rule border border-rule">
                            <Stat label={isFormateur ? "Bilans (total)" : "Mes bilans"} value={bilans.length} testId="stat-bilans-count" />
                            <Stat label="Avec photos" value={bilans.filter((b) => (b.photo_paths?.length || 0) > 0).length} testId="stat-photos-count" />
                            <Stat label="Tuteurs évalués" value={bilans.filter((b) => b.tutor_evaluation).length} testId="stat-tutor-count" />
                            <div className="bg-paper p-6">
                                <div className="label-eyebrow">Plateforme</div>
                                <div className="h-display text-2xl font-semibold mt-2 flex items-center gap-2">
                                    {isResponsable ? (
                                        <><CheckCircle2 size={20} className="text-emerald-600" /> Accès activé</>
                                    ) : (
                                        user?.platform_subscription_active ? (
                                            <><CheckCircle2 size={20} className="text-emerald-600" /> Active</>
                                        ) : (
                                            <span className="text-destructive inline-flex items-center gap-1"><Lock size={16} />Verrouillée</span>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </Accordion>
                </div>

                {/* LIST */}
                <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
                    <div>
                        <div className="label-eyebrow">{isFormateur ? "Tous les bilans" : "Historique"}</div>
                        <h2 className="h-display text-2xl font-semibold">
                            {viewMode === "tree"
                                ? "Archives par année scolaire"
                                : `${filtered.length} bilan${filtered.length > 1 ? "s" : ""}`}
                        </h2>
                    </div>
                    {/* VIEW MODE TOGGLE */}
                    <div className="inline-flex border border-rule overflow-hidden" data-testid="dashboard-view-toggle">
                        <button
                            type="button"
                            onClick={() => setView("tree")}
                            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors ${viewMode === "tree" ? "bg-ink text-white" : "bg-paper hover:bg-orange/5"}`}
                            data-testid="dashboard-view-tree"
                        >
                            <FolderTree size={13} /> Dossiers
                        </button>
                        <button
                            type="button"
                            onClick={() => setView("list")}
                            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 transition-colors border-l border-rule ${viewMode === "list" ? "bg-ink text-white" : "bg-paper hover:bg-orange/5"}`}
                            data-testid="dashboard-view-list"
                        >
                            <List size={13} /> Liste
                        </button>
                    </div>
                </div>

                {viewMode === "tree" ? (
                    <BilansTreeView key={treeReloadKey} isFormateur={isFormateur} onNiveauChange={treeReloadKey} />
                ) : loading ? (
                    <div className="panel p-10 text-center text-muted2 text-sm">Chargement…</div>
                ) : bilans.length === 0 ? (
                    <div className="panel p-12 text-center" data-testid="dashboard-empty-state">
                        <Inbox size={36} strokeWidth={1.5} className="text-muted2 mx-auto" />
                        <h3 className="h-display text-xl font-semibold mt-4">
                            {isFormateur ? "Aucun bilan n'a encore été soumis" : "Aucun bilan pour l'instant"}
                        </h3>
                        <p className="text-sm text-muted2 mt-2 max-w-md mx-auto">
                            {isFormateur ? "Demandez à vos alternants de créer un compte gratuit et de soumettre leur premier bilan." : "Créez votre premier bilan en suivant les 6 étapes guidées."}
                        </p>
                        {!isFormateur && (
                            <Link to="/bilans/nouveau" className="btn-primary mt-6 inline-flex">
                                <Plus size={16} /> Créer un bilan
                            </Link>
                        )}
                    </div>
                ) : (
                    <div className="space-y-10">
                        {grouped.map((g) => (
                            <div key={g.key}>
                                {g.label && (
                                    <div className="flex items-baseline justify-between mb-3 pb-2 border-b-2 border-ink">
                                        <h3 className="h-display text-xl font-semibold flex items-center gap-2">
                                            <span className="text-orange font-mono text-xs">●</span> {g.label}
                                        </h3>
                                        <span className="text-xs font-mono text-muted2">{g.items.length} bilan{g.items.length > 1 ? "s" : ""}</span>
                                    </div>
                                )}
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {g.items.map((b) => (
                                        <BilanCard key={b.id} b={b} isFormateur={isFormateur} onNiveauChange={reloadBilans} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}

const Stat = ({ label, value, testId }) => (
    <div className="bg-paper p-6">
        <div className="label-eyebrow">{label}</div>
        <div className="h-display text-4xl font-bold mt-2" data-testid={testId}>{value}</div>
    </div>
);

const AlternantCard = ({ a, onNiveauChange }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const switchNiveau = async (e, target) => {
        e.preventDefault();
        e.stopPropagation();
        setMenuOpen(false);
        try {
            await api.patch(`/establishment/alternants/${a.id}/niveau`, { niveau: target });
            toast.success(`Niveau mis à jour : ${target === "1ere" ? "1ère année" : "2ème année"}`);
            onNiveauChange && onNiveauChange();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        }
    };
    return (
        <Link
            to={`/progression/${a.id}`}
            className="border border-rule p-3 hover:border-orange hover:bg-orange/5 transition-all relative"
            data-testid={`alternant-card-${a.id}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-sm flex-1 min-w-0 truncate">
                    {a.prenom} {a.nom}
                </div>
                <div className="relative shrink-0" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                    <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen((v) => !v); }}
                        className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors cursor-pointer !px-1.5 !py-0 text-[10px]"
                        title="Changer le niveau"
                        data-testid={`alternant-niveau-pill-${a.id}`}
                    >
                        {a.niveau === "2eme" ? "2ème" : a.niveau === "1ere" ? "1ère" : "—"} ▾
                    </button>
                    {menuOpen && (
                        <div className="absolute right-0 top-full mt-1 z-20 bg-paper border border-rule shadow-lg min-w-[140px]" data-testid={`alternant-niveau-menu-${a.id}`}>
                            <button type="button" disabled={a.niveau === "1ere"} onClick={(e) => switchNiveau(e, "1ere")} className="block w-full text-left px-3 py-2 text-xs hover:bg-orange/10 disabled:bg-orange/15 disabled:font-bold disabled:cursor-default" data-testid={`alternant-set-niveau-1ere-${a.id}`}>1ère année</button>
                            <button type="button" disabled={a.niveau === "2eme"} onClick={(e) => switchNiveau(e, "2eme")} className="block w-full text-left px-3 py-2 text-xs hover:bg-orange/10 disabled:bg-orange/15 disabled:font-bold disabled:cursor-default border-t border-rule" data-testid={`alternant-set-niveau-2eme-${a.id}`}>2ème année</button>
                        </div>
                    )}
                </div>
            </div>
            <div className="text-[11px] font-mono text-muted2 truncate">{a.email}</div>
            {a.phone && <div className="text-[11px] font-mono text-muted2 mt-0.5">{a.phone}</div>}
            <div className="mt-2 text-[10px] font-mono text-orange inline-flex items-center gap-1">
                <TrendingUp size={10} /> Voir progression
            </div>
        </Link>
    );
};

const BilanCard = ({ b, isFormateur, onNiveauChange }) => {
    const alerts = isFormateur ? computeAlerts(b) : [];
    const hasHigh = alerts.some((a) => a.level === "high");
    const [menuOpen, setMenuOpen] = useState(false);

    const switchNiveau = async (e, target) => {
        e.preventDefault();
        e.stopPropagation();
        setMenuOpen(false);
        try {
            // Use the per-bilan endpoint so the snapshot on the bilan itself
            // is updated AND (if it exists) the alternant user's niveau is
            // cascaded — this fixes "the pill changes but the bilan still
            // shows the old niveau" because the bilan stores its own snapshot.
            await api.patch(`/bilans/${b.id}/niveau`, { niveau: target });
            toast.success(`Niveau mis à jour : ${target === "1ere" ? "1ère année" : "2ème année"}`);
            onNiveauChange && onNiveauChange();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur");
        }
    };

    return (
        <Link
            to={`/bilans/${b.id}`}
            className={`panel p-5 hover:border-ink transition-colors group relative ${hasHigh ? "border-l-4 border-l-destructive" : ""}`}
            data-testid={`bilan-card-${b.id}`}
        >
            {hasHigh && (
                <span className="absolute -top-2 -right-2 bg-destructive text-white text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase tracking-wider flex items-center gap-1">
                    <AlertTriangle size={10} /> Alerte
                </span>
            )}
            <div className="flex items-start justify-between gap-3">
                <div className="label-eyebrow text-orange truncate">{FILIERE_LABELS[b.filiere] || b.filiere}</div>
                {isFormateur ? (
                    <div className="relative" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                        <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen((v) => !v); }}
                            className="pill bg-ink text-white hover:bg-orange transition-colors cursor-pointer"
                            title="Changer le niveau de l'alternant"
                            data-testid={`bilan-niveau-pill-${b.id}`}
                        >
                            {b.niveau === "1ere" ? "1ère" : "2ème"} ▾
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 top-full mt-1 z-20 bg-paper border border-rule shadow-lg min-w-[140px]" data-testid={`bilan-niveau-menu-${b.id}`}>
                                <button type="button" disabled={b.niveau === "1ere"} onClick={(e) => switchNiveau(e, "1ere")} className="block w-full text-left px-3 py-2 text-xs hover:bg-orange/10 disabled:bg-orange/15 disabled:font-bold disabled:cursor-default" data-testid={`bilan-set-niveau-1ere-${b.id}`}>1ère année</button>
                                <button type="button" disabled={b.niveau === "2eme"} onClick={(e) => switchNiveau(e, "2eme")} className="block w-full text-left px-3 py-2 text-xs hover:bg-orange/10 disabled:bg-orange/15 disabled:font-bold disabled:cursor-default border-t border-rule" data-testid={`bilan-set-niveau-2eme-${b.id}`}>2ème année</button>
                            </div>
                        )}
                    </div>
                ) : (
                    <span className="pill bg-ink text-white">{b.niveau === "1ere" ? "1ère" : "2ème"}</span>
                )}
            </div>
            <h3 className="h-display text-xl font-semibold mt-3">{b.prenom} {b.nom}</h3>
            <div className="mt-4 space-y-1.5 text-xs text-ink/70 font-mono">
                {b.entreprise && <div className="flex items-center gap-2"><Building2 size={13} className="text-muted2" /> {b.entreprise}</div>}
                <div className="flex items-center gap-2"><GraduationCap size={13} className="text-muted2" /> Tuteur · {b.tuteur || "—"}</div>
                <div className="flex items-center gap-2"><Calendar size={13} className="text-muted2" /> {b.date_debut} → {b.date_fin}</div>
                {isFormateur && b.author_email && <div className="flex items-center gap-2 text-muted2 truncate"><Eye size={13} /> {b.author_email}</div>}
                {b.tutor_evaluation && <div className="flex items-center gap-2 text-emerald-700"><Briefcase size={13} /> Tuteur évalué ✓</div>}
                {!b.tutor_evaluation && b.tutor_reminder_sent_at && (
                    <div className="flex items-center gap-2 text-orange" data-testid={`bilan-tutor-reminder-badge-${b.id}`}>
                        <Bell size={12} /> Rappel envoyé le {new Date(b.tutor_reminder_sent_at).toLocaleDateString("fr-FR")}
                        {b.tutor_reminders_count > 1 && ` (×${b.tutor_reminders_count})`}
                    </div>
                )}
            </div>
            {/* Manual "remind tutor" button — visible on any bilan awaiting
                a tutor evaluation and whose alternant used the send-by-email flow. */}
            {isFormateur && !b.tutor_evaluation && b.sent_to_email && (
                <button
                    type="button"
                    onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                            await api.post(`/bilans/${b.id}/tutor-reminder`);
                            toast.success(`Rappel envoyé à ${b.sent_to_email}`);
                            onNiveauChange && onNiveauChange();
                        } catch (err) {
                            toast.error(err.response?.data?.detail || "Erreur d'envoi");
                        }
                    }}
                    className="mt-3 btn-outline !text-[10px] !px-3 !py-1.5 inline-flex items-center gap-1"
                    title={`Renvoyer un rappel à ${b.sent_to_email}`}
                    data-testid={`bilan-tutor-reminder-${b.id}`}
                >
                    <Bell size={11} /> Rappel tuteur
                </button>
            )}
            <div className="mt-5 flex items-center justify-between text-xs">
                <span className="font-mono text-muted2">
                    {b.selected_competencies?.length || 0} compétences · {b.photo_paths?.length || 0} photos
                </span>
                {isFormateur && b.user_id && (
                    <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); window.location.href = `/progression/${b.user_id}`; }}
                        className="text-[10px] font-mono text-orange hover:underline"
                        data-testid={`bilan-progression-${b.id}`}
                    >
                        Voir progression →
                    </button>
                )}
                <ArrowRight size={14} className="text-orange opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </div>
        </Link>
    );
};
